import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'
import { useTranslation } from 'react-i18next'

import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Grid from '@material-ui/core/Grid'
import CircularProgress from '@material-ui/core/CircularProgress'

import { KeyboardDatePicker } from '@material-ui/pickers'
import { format, parse, parseISO, isValid } from 'date-fns'

import { useParcelsResolutionsDates } from '../../../../adapters/parcels'
import { BasicStatsParcel } from './BasicStatsParcel'
import { useAuth } from '../../../../context/AuthContext'
import { getLayerWithPriority } from '@graniot/common/utils'

import LittleMap from './LittleMap'
import LittleWMSMap from './LittleWMSMap'
import { LayerSelector } from '../LayerSelector'
import { MaxCloudCoverageSelector } from '../MaxCloudCoverageSelector'
import CenteredProgress from '../../../../utils/centeredprogress'
import Alert from '@material-ui/lab/Alert';

import { find } from 'lodash'

const useStyles = makeStyles((theme) => ({
  imgParcel: {
    maxHeight: 400,
    padding: theme.spacing(1),
  },
  imgContainer: {
    minHeight: 360,
  },
  divider: {
    margin: theme.spacing(2),
  },
  pickers: {
    marginTop: theme.spacing(2),
  },
  loading: {
    marginBottom: theme.spacing(20),
    marginTop: theme.spacing(20),
  },
  small: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
  },
}))

function ComparatorContent({
  layers,
  parcel,
  farms,
  baseImage,
  setBaseImage,
  comparedImage,
  setComparedImage,
  selectedResolution,
  width,
}) {
  const classes = useStyles()
  const { t } = useTranslation()
  const { user } = useAuth()

  const [selectedBaseLayer, setSelectedBaseLayer] = React.useState(getLayerWithPriority(layers))
  const [selectedComparedLayer, setSelectedComparedLayer] = React.useState(getLayerWithPriority(layers))

  const [maxCC, setMaxCC] = React.useState(0.9)
  const [from_date, setFromDate] = React.useState(
    `01/01/${selectedResolution.resolution === "10x10 meters" || selectedResolution.resolution === "1x1 meter"
      ? 2017
      : selectedResolution.id === 2
        ? 2019
        : 2021
    }`
  )

  const [baseMap, setBaseMap] = React.useState(null)
  const [comparedMap, setComparedMap] = React.useState(null)

  const baseRef = React.useRef()
  const comparedRef = React.useRef()

  const [baseFeatureStats, setBaseFeatureStats] = React.useState(null)
  const [comparedFeatureStats, setComparedFeatureStats] = React.useState(null)

  const [toDate, setToDate] = React.useState(format(new Date(), 'yyyy-LL-dd'))

  const [baseDateValue, setBaseDateValue] = React.useState(baseImage?.date)
  const [comparedDateValue, setComparedDateValue] = React.useState(
    baseImage?.date
  )

  const [baseErrorStatus, setBaseErrorStatus] = React.useState(false)
  const [comparedErrorStatus, setComparedErrorStatus] = React.useState(false)
  const errorMessage = t('Invalid_Date')

  const layersMatch = selectedBaseLayer.key === selectedComparedLayer.key

  const mapHeight = 'calc(100vh - 360px)'

  const {
    isLoading: isLoadingBase,
    data: dataBase,
    refetch: refetchBase,
    error: errorBase,
  } = useParcelsResolutionsDates(
    user,
    parcel,
    selectedBaseLayer,
    from_date
  )

  const {
    isLoading: isLoadingComparedRaw,
    data: dataComparedRaw,
    refetch: refetchCompared,
    error: errorComparedRaw,
  } = useParcelsResolutionsDates(
    user,
    parcel,
    selectedComparedLayer,
    from_date,
    { enabled: !layersMatch }
  )

  const isLoadingCompared = layersMatch ? isLoadingBase : isLoadingComparedRaw
  const dataCompared = layersMatch ? dataBase : dataComparedRaw
  const errorCompared = layersMatch ? errorBase : errorComparedRaw

  const baseHasNoData = !isLoadingBase && Array.isArray(dataBase) && dataBase.length === 0
  const comparedHasNoData = !isLoadingCompared && Array.isArray(dataCompared) && dataCompared.length === 0

  // Check for parcel size limit errors
  const parcelSizeError = errorBase?.error_code === 'PARCEL_SIZE_EXCEEDS_LIMIT' ? errorBase
                        : errorCompared?.error_code === 'PARCEL_SIZE_EXCEEDS_LIMIT' ? errorCompared
                        : null;
  const statsLimitError = errorBase?.error_code === 'PARCEL_EXCEEDS_STATS_LIMIT' ? errorBase
                     : errorCompared?.error_code === 'PARCEL_EXCEEDS_STATS_LIMIT' ? errorCompared
                     : null;

  React.useEffect(() => {
    if (baseMap && comparedMap) {
      try {
        baseMap.sync(comparedMap)
        comparedMap.sync(baseMap)
      } catch (error) {
        console.error(error)
      }
    }
  }, [baseMap, comparedMap])

  React.useEffect(() => {
    const filterByCloudCoverage = (data, maxCC) => 
      data.filter(x => {
        // Include the item if cloud_coverage is missing or "N/A"
        if (!x?.cloud_coverage || x.cloud_coverage === "N/A") return true;
  
        // Convert "90%" to 0.9 and compare with maxCC
        const cloudCoverageValue = parseFloat(x.cloud_coverage.replace('%', '')) / 100;
        return cloudCoverageValue <= maxCC;
      });
  
    if (dataBase) {
      let base_feature_stats = Object.assign([], filterByCloudCoverage(dataBase, maxCC));
      base_feature_stats.reverse();
      setBaseFeatureStats(base_feature_stats);
    }
  
    if (dataCompared) {
      let compared_feature_stats = Object.assign([], filterByCloudCoverage(dataCompared, maxCC));
      compared_feature_stats.reverse();
      setComparedFeatureStats(compared_feature_stats);
    }
  
  }, [dataBase, dataCompared, maxCC]);

  React.useEffect(() => {
    if (
      baseFeatureStats &&
      comparedFeatureStats &&
      baseFeatureStats.length > 1 &&
      comparedFeatureStats.length > 1
    ) {
      if (
        baseImage?.date &&
        find(baseFeatureStats, { date: baseImage.date })
      )
        setBaseImage(find(baseFeatureStats, { date: baseImage.date }))
      else setBaseImage(baseFeatureStats[baseFeatureStats.length - 1])

      if (
        comparedImage?.date &&
        find(comparedFeatureStats, { date: comparedImage.date })
      )
        setComparedImage(
          find(comparedFeatureStats, { date: comparedImage.date })
        )
      else if (comparedFeatureStats.length === 1) {
        setComparedImage(
          comparedFeatureStats[comparedFeatureStats.length - 1]
        )
      } else {
        if (
          comparedFeatureStats[
            comparedFeatureStats.length - 1
          ].date.slice(0, 10) ===
          comparedFeatureStats[
            comparedFeatureStats.length - 2
          ].date.slice(0, 10)
        )
          setComparedImage(
            comparedFeatureStats[comparedFeatureStats.length - 3]
          )
        else
          setComparedImage(
            comparedFeatureStats[comparedFeatureStats.length - 2]
          )
      }
    } else if (
      baseFeatureStats?.length === 1 ||
      comparedFeatureStats?.length === 1
    ) {
      if (baseFeatureStats?.length === 1) setBaseImage(baseFeatureStats[0])
      if (comparedFeatureStats?.length === 1)
        setComparedImage(comparedFeatureStats[0])
    } else {
      setBaseImage(null)
      setComparedImage(null)
    }
  }, [baseFeatureStats, comparedFeatureStats])

  // With a single available date there is nothing to compare against: hide
  // the compared side and let the base side take the full width.
  const hideCompared = layersMatch && baseFeatureStats?.length === 1

  React.useEffect(() => {
    if (hideCompared) setComparedMap(null)
  }, [hideCompared])

  const handleCloudCoverageChange = (event, newValue) => {
    setMaxCC(newValue)
  }

  const handleSelectedBaseLayer = (event, newValue) => {
    setSelectedBaseLayer(newValue)
    // The compared side is hidden, so keep its layer in step with the base one
    if (hideCompared) setSelectedComparedLayer(newValue)
  }

  const handleSelectedComparedLayer = (event, newValue) => {
    setSelectedComparedLayer(newValue)
  }

  function indexOfDate2(date, featureStats) {
    return featureStats.findIndex((d) => {
      return new Date(d.date) - parse(date, 'dd/MM/yyyy', new Date()) > 0
    })
  }

  function indexOfDate(date) {
    return comparedFeatureStats
      .map((d) => format(new Date(d.date), 'dd/MM/yyyy'))
      .indexOf(date)
  }

  const getCLosestDate = (date, features) => {
    let closest = { value: Infinity, date: null }
    features.forEach((feature) => {
      let tmp = Math.abs(date - parseISO(feature.date, 'dd/MM/yyyy'))
      if (tmp < closest.value) {
        closest.value = tmp
        closest.date = feature.date
      }
    })
    return new Date(closest.date)
  }

  const handleChangeBaseDate = (date, e) => {
    if (isValid(date)) {
      setBaseErrorStatus(false)
      let baseDate = format(
        getCLosestDate(date, baseFeatureStats),
        'dd/MM/yyyy'
      )
      try {
        baseMap.unsync(comparedMap)
        comparedMap.unsync(baseMap)
        setBaseImage(
          baseFeatureStats[indexOfDate2(baseDate, baseFeatureStats)]
        )
        setBaseDateValue(baseDate)
      } catch (error) {
        console.error(error)
      }
    } else {
      setBaseDateValue(e)
      setBaseErrorStatus(true)
    }
  }

  const handleChangeComparedDate = (date, e) => {
    if (isValid(date)) {
      setComparedErrorStatus(false)
      let comparedDate = format(
        getCLosestDate(date, comparedFeatureStats),
        'dd/MM/yyyy'
      )
      try {
        baseMap.unsync(comparedMap)
        comparedMap.unsync(baseMap)
        setComparedImage(
          comparedFeatureStats[
          indexOfDate2(comparedDate, comparedFeatureStats)
          ]
        )
        setComparedDateValue(comparedImage)
      } catch (error) {
        console.error(error)
      }
    } else {
      setComparedDateValue(e)
      setComparedErrorStatus(true)
    }
  }

  function disableNotAvalibleSentinelImages(date) {
    return indexOfDate(date) === -1
  }

  return (
    <React.Fragment>
      { parcelSizeError ?
        <Alert severity="error">
          {t('parcel_size_exceeded', {
            hectares: parcelSizeError.details?.parcel_hectares || 'N/A',
            maxLimit: parcelSizeError.details?.max_limit || 'N/A'
          })}
        </Alert>
      : statsLimitError ?
        <Alert severity="warning">{t('parcel_exceeds_stats_limit')}</Alert>
      :
        <React.Fragment>
          {selectedBaseLayer.is_sentinel &&
            selectedComparedLayer.is_sentinel &&
            ['10x10 meters', '3x3 meters'].includes(selectedResolution.resolution) && (
              <Grid
                container
                direction="row"
                style={{ marginBottom: 20 }}
              >
                <Grid item xs={12} sm={6}>
                  <Box
                    display="flex"
                    justifyContent={
                      isWidthUp('sm', width) ? 'start' : 'center'
                    }
                  >
                      <MaxCloudCoverageSelector
                        maxCC={maxCC}
                        handleCloudCoverageChange={
                          handleCloudCoverageChange
                        }
                        info={true}
                        disabled={!dataBase?.every(item => item.hasOwnProperty('cloud_coverage'))}
                      />
                  </Box>
                </Grid>
              </Grid>
            )}
        <Box px={{ xs: 1, sm: 2 }}>
          <Grid container spacing={2} alignItems="flex-start">
            {!hideCompared && (
            <Grid item xs={6}>
              <Paper style={{ marginBottom: 16 }}>
                <Grid
                  container
                  justifyContent="center"
                  className={classes.imgContainer}
                >
                  {isLoadingCompared && !comparedImage ? (
                    <Grid item xs={3} align="center">
                      <CircularProgress className={classes.loading} />
                    </Grid>
                  ) : comparedHasNoData ? (
                    <Grid item xs={12} style={{ padding: 16 }}>
                      <Alert severity="info">{t('No data Found')}</Alert>
                    </Grid>
                  ) : selectedComparedLayer.is_sentinel ? (
                    <LittleMap
                      baseRef={baseRef}
                      comparedRef={comparedRef}
                      parcel={parcel}
                      farms={farms}
                      scrData={comparedImage}
                      map={comparedMap}
                      setMap={setComparedMap}
                      layer={selectedComparedLayer}
                      selectedResolution={selectedResolution}
                      comparedMap={baseMap}
                      height={mapHeight}
                    />
                  ) : (
                    <LittleWMSMap
                      baseRef={baseRef}
                      comparedRef={comparedRef}
                      parcel={parcel}
                      farms={farms}
                      scrData={comparedImage}
                      map={comparedMap}
                      setMap={setComparedMap}
                      layer={selectedComparedLayer}
                      selectedResolution={selectedResolution}
                      comparedMap={baseMap}
                      height={mapHeight}
                    />
                  )}
                </Grid>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <LayerSelector
                    layers={layers}
                    selectedLayer={selectedComparedLayer}
                    handleSelectedLayer={handleSelectedComparedLayer}
                    width={'100%'}
                  />
                </Grid>
                {comparedImage && (
                  <React.Fragment>
                    <Grid item xs={12} md={6}>
                      <KeyboardDatePicker
                        label={t(`Note.Layer date`)}
                        name="compared_date"
                        value={comparedImage.date}
                        onChange={(date, e) =>
                          handleChangeComparedDate(date, e)
                        }
                        shouldDisableDate={(date) =>
                          disableNotAvalibleSentinelImages(
                            format(date, 'dd/MM/yyyy')
                          )
                        }
                        maxDate={new Date()}
                        minDate={new Date(from_date)}
                        format="dd/MM/yyyy"
                        error={comparedErrorStatus}
                        helperText={
                          comparedErrorStatus ? errorMessage : null
                        }
                        inputProps={{ readOnly: isLoadingBase }}
                        variant="inline"
                        fullWidth
                        autoOk
                      />
                    </Grid>
                    {!comparedImage?.date ? (
                      <Grid item xs={12}>
                        <CenteredProgress />
                      </Grid>
                    ) : (
                      selectedComparedLayer.is_sentinel && (
                        <Grid item xs={12}>
                          <BasicStatsParcel
                            layer={selectedComparedLayer}
                            parcel={parcel}
                            stats={comparedImage}
                          />
                        </Grid>
                      )
                    )}
                  </React.Fragment>
                )}
              </Grid>
            </Grid>
            )}

            <Grid item xs={hideCompared ? 12 : 6}>
              <Paper style={{ marginBottom: 16 }}>
                <Grid
                  container
                  justifyContent="center"
                  className={classes.imgContainer}
                >
                  {isLoadingBase && !baseImage ? (
                    <Grid item xs={3} align="center">
                      <CircularProgress className={classes.loading} />
                    </Grid>
                  ) : baseHasNoData ? (
                    <Grid item xs={12} style={{ padding: 16 }}>
                      <Alert severity="info">{t('No data Found')}</Alert>
                    </Grid>
                  ) : selectedBaseLayer.is_sentinel ? (
                    <LittleMap
                      baseRef={comparedRef}
                      comparedRef={baseRef}
                      parcel={parcel}
                      farms={farms}
                      scrData={baseImage}
                      map={baseMap}
                      setMap={setBaseMap}
                      layer={selectedBaseLayer}
                      selectedResolution={selectedResolution}
                      comparedMap={comparedMap}
                      height={mapHeight}
                    />
                  ) : (
                    <LittleWMSMap
                      baseRef={comparedRef}
                      comparedRef={baseRef}
                      parcel={parcel}
                      farms={farms}
                      scrData={baseImage}
                      map={baseMap}
                      setMap={setBaseMap}
                      layer={selectedBaseLayer}
                      selectedResolution={selectedResolution}
                      comparedMap={comparedMap}
                      height={mapHeight}
                    />
                  )}
                </Grid>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <LayerSelector
                    layers={layers}
                    selectedLayer={selectedBaseLayer}
                    handleSelectedLayer={handleSelectedBaseLayer}
                    width={'100%'}
                  />
                </Grid>
                {baseImage && (
                  <React.Fragment>
                    <Grid item xs={12} md={6}>
                      <KeyboardDatePicker
                        label={t(`Note.Layer date`)}
                        name="base_date"
                        value={baseImage.date}
                        inputValue={baseDateValue}
                        onChange={(date, e) =>
                          handleChangeBaseDate(date, e)
                        }
                        shouldDisableDate={(date) =>
                          disableNotAvalibleSentinelImages(
                            format(date, 'dd/MM/yyyy')
                          )
                        }
                        maxDate={new Date()}
                        minDate={new Date(from_date)}
                        format="dd/MM/yyyy"
                        error={baseErrorStatus}
                        helperText={
                          baseErrorStatus ? errorMessage : null
                        }
                        inputProps={{ readOnly: isLoadingBase }}
                        variant="inline"
                        fullWidth
                        autoOk
                      />
                    </Grid>
                    {!baseImage?.date ? (
                      <Grid item xs={12}>
                        <CenteredProgress />
                      </Grid>
                    ) : (
                      selectedBaseLayer.is_sentinel && (
                        <Grid item xs={12}>
                          <BasicStatsParcel
                            layer={selectedBaseLayer}
                            parcel={parcel}
                            stats={baseImage}
                          />
                        </Grid>
                      )
                    )}
                  </React.Fragment>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Box>
        </React.Fragment>
      }
    </React.Fragment>
  )
}

export function isComparableLayer(layer) {
  const resolution = layer?.layer_resolution?.resolution

  if (['10x10 meters', '3x3 meters'].includes(resolution)) {
    return Boolean(layer?.key)
  }

  return Boolean(layer?.layer_resolution?.key)
}

export function Comparator(props) {
  const { t } = useTranslation()
  const comparableLayers = (props.layers || []).filter(isComparableLayer)

  if (comparableLayers.length === 0) {
    return (
      <Box px={{ xs: 1, sm: 2 }} py={2}>
        <Alert severity="info">{t('No_layers_for_resolution')}</Alert>
      </Box>
    )
  }

  return <ComparatorContent {...props} layers={comparableLayers} />
}

export default withWidth()(Comparator)
